import { useState } from "react";
import { Send, Loader2, Megaphone } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const TYPES = [
  { id: "announcement", label: "Announcement" },
  { id: "system", label: "System" },
  { id: "billing", label: "Billing" },
  { id: "payment", label: "Payment" },
  { id: "usage", label: "Usage" },
  { id: "transcription", label: "Transcription" },
  { id: "extension", label: "Extension" },
  { id: "desktop", label: "Desktop" },
];

export default function AdminBroadcastPage() {
  const [form, setForm] = useState({ title: "", body: "", type: "announcement", link: "" });
  const [busy, setBusy] = useState(false);
  const [lastSent, setLastSent] = useState(null);

  const send = async (e) => {
    e.preventDefault();
    if (!form.title || !form.body) { toast.error("Title and body are required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/notifications/broadcast", form);
      setLastSent({ ...form, count: data.sent });
      setForm({ title: "", body: "", type: "announcement", link: "" });
      toast.success(`Sent to ${data.sent} active users`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="px-5 lg:px-10 py-8 max-w-3xl mx-auto">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-warm flex items-center justify-center text-white"><Megaphone className="w-5 h-5" /></div>
        <div>
          <h1 className="text-3xl font-bold text-brand-navy">Broadcast notification</h1>
          <p className="text-sm text-brand-muted mt-1">Push a notification to every active user's bell.</p>
        </div>
      </div>

      <form onSubmit={send} className="mt-6 bg-white rounded-2xl border border-brand-borderc p-6 space-y-3">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-brand-muted">Title</label>
          <input data-testid="broadcast-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. New: Telugu support is live!" className="mt-1 w-full px-3 py-2.5 rounded-xl border border-brand-borderc focus:border-brand-purple outline-none" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-brand-muted">Message</label>
          <textarea data-testid="broadcast-body" required rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Tell users what's new in one or two sentences." className="mt-1 w-full px-3 py-2.5 rounded-xl border border-brand-borderc focus:border-brand-purple outline-none resize-none" />
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-brand-muted">Type</label>
            <select data-testid="broadcast-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-brand-borderc focus:border-brand-purple outline-none">
              {TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-brand-muted">Optional link</label>
            <input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="/app/billing" className="mt-1 w-full px-3 py-2.5 rounded-xl border border-brand-borderc focus:border-brand-purple outline-none" />
          </div>
        </div>
        <button data-testid="broadcast-send" disabled={busy} className="btn-primary-gradient rounded-full px-6 py-3 text-sm font-semibold inline-flex items-center gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Broadcast to all users
        </button>
      </form>

      {lastSent && (
        <div className="mt-5 bg-white rounded-2xl border border-brand-borderc p-5">
          <div className="text-xs uppercase tracking-widest font-semibold text-brand-muted">Last broadcast</div>
          <div className="mt-2 font-semibold text-brand-navy">{lastSent.title}</div>
          <div className="text-sm text-brand-text mt-1">{lastSent.body}</div>
          <div className="mt-2 text-xs text-brand-muted">Sent to <strong>{lastSent.count}</strong> users · type: {lastSent.type}</div>
        </div>
      )}
    </div>
  );
}
